/**
 * This is the Purchase Order List comoponent that allows the user to view all
 * of the purchase orders, as well as accepting the transaction from the
 * individual row component.
 */
import { chain, assign, get, isEmpty, includes } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import queryString from 'querystring';

import { Button, Icon, HideText } from '@fashionnovateam/nova-ui-toolkit';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';
import historyProps from '../../Lib/Utils/PropTypes/history-props';
import { deliveriesExport } from '../../Lib/Utils/Table/table-export';

import RemoveBookingModal from './RemoveBookingModal';
import DeliveryModal from './DeliveryModal';
import UserRoles, {
  Actions,
  hasPermissionTo,
} from '../../Lib/Constants/roles.ts';
import vendorProps from '../../Lib/Utils/PropTypes/vendor-props';
import isSelfScheduler from '../../Lib/Utils/Role/isSelfScheduler';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext.tsx';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';
import VendorDeliveriesDataGrid from './VendorDeliveriesDataGrid.tsx';

const getSortedBy = (location) => {
  const params = decodeLocation(location.search);
  return params.sortBy;
};

export default function VendorDeliveries(props) {
  const {
    availabilities,
    deliverablePurchaseOrders,
    history,
    loading,
    location,
    deliveries,
    count,
    currentUser,
    getDeliverablePurchaseOrders,
    getDeliveries,
    createDelivery,
    updateDelivery,
    removeDelivery,
    locations,
    getLocations,
    getAvailabilities,
    vendor,
  } = props;

  const targetUserId = location.pathname.split('/')[2];
  const params = decodeLocation(location.search);

  const [exporting, setExporting] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [openRemoveModal, toggleRemoveModal] = useState(false);
  const [openDeliveryModal, toggleDeliveryModal] = useState(false);
  const [, setSortedBy] = useState(getSortedBy(location));
  const [initialFetch, setInitialFetch] = useState(false);
  const { flags } = React.useContext(FeatureFlagsContext);

  useEffect(() => {
    if (initialFetch) return;
    getDeliveries(targetUserId, location.search.slice(1));
    getLocations();
    setInitialFetch(true);
  }, [
    initialFetch,
    getLocations,
    getDeliveries,
    targetUserId,
    location.search,
  ]);

  /**
   * A function that updates the url when different parameters in the component
   * are updated. Calls getDeliveries to get all deliveries that
   * match the params.
   * @param {string} name the name of the parameter to be added to the url
   * @param {string} value the actual parameter we are passing back
   */
  function updateUrl(name, value) {
    const { search: queryParams, pathname } = location;

    const search = chain(queryParams.slice(1))
      .thru(queryString.parse)
      .assign({ [name]: value })
      .thru((p) => (name === 'search' ? assign(p, { page: 1 }) : p))
      .pickBy()
      .thru(queryString.encode)
      .value();

    history.push({ pathname, search });

    getDeliveries(targetUserId, search);
  }

  /**
   * A function that updates the url when different parameters in the component
   * are updated. Calls getDeliveries to get all deliveries that
   * match the params. Called when sort is altered using table headers
   * @param {string} sortBy the name of the parameter to be added to the url
   * @param {string} sortOrder the order parameter (ex. 'ASC'/'DESC')
   */
  function updateUrlHeader(e, field) {
    const { search: queryParams, pathname } = location;
    const sortParams = decodeLocation(queryParams);
    const { sortBy: sb, sortOrder } = sortParams;

    let newSortOrder;
    if (!sortOrder || field !== sb) {
      newSortOrder = sortOrder || 'ASC';
    } else {
      newSortOrder = sortOrder === 'DESC' ? 'ASC' : 'DESC';
    }

    const search = chain(queryParams.slice(1))
      .thru(queryString.parse)
      .assign({ sortBy: field, sortOrder: newSortOrder })
      .pickBy()
      .thru(queryString.encode)
      .value();
    setSortedBy(field);
    history.push({ pathname, search });

    getDeliveries(targetUserId, search);
  }

  function removeSortParams() {
    const { search: queryParams, pathname } = location;
    const search = chain(queryParams.slice(1))
      .thru(queryString.parse)
      .assign({ sortBy: undefined, sortOrder: undefined })
      .pickBy()
      .thru(queryString.encode)
      .value();

    history.push({ pathname, search });

    getDeliveries(targetUserId, search);
  }

  const exportDeliveries = () => {
    setExporting(true);

    deliveriesExport(targetUserId).then(() => setExporting(false));
  };

  const saveDeliveryV2 = (delivery, purchaseOrderNumbers) => {
    if (delivery.id) {
      updateDelivery(targetUserId, delivery.id, delivery, purchaseOrderNumbers);
    } else {
      createDelivery(
        targetUserId,
        { ...delivery, id: undefined },
        purchaseOrderNumbers,
      );
    }

    toggleDeliveryModal(!openDeliveryModal);
    setSelectedDelivery(null);
  };

  const perms = useMemo(() => {
    const canScheduleDelivery =
      isSelfScheduler(vendor.email) &&
      flags.self_scheduled_deliveries &&
      flags.self_scheduled_deliveries.enabled &&
      hasPermissionTo(
        'users/:id/deliveries',
        currentUser.role,
        Actions.CREATE,
        'DELIVERY',
      );
    const canExport = hasPermissionTo(
      'users/:id/deliveries',
      currentUser.role,
      Actions.READ,
      'EXPORT',
    );
    return {
      canScheduleDelivery,
      canExport,
    };
  }, [vendor.email, flags.self_scheduled_deliveries, currentUser]);

  return (
    <Page.Container>
      <Header.Major icon="truck/truck-default" title="Deliveries">
        {perms.canScheduleDelivery ? (
          <Button
            size="sm"
            color="secondary"
            onClick={() => toggleDeliveryModal(!openDeliveryModal)}
          >
            <Icon name="truck/truck-default" />
            <HideText notAbove="lg">Schedule{'\u00A0'}Delivery</HideText>
          </Button>
        ) : null}
        {exporting ? (
          <Icon.LoadingCircle />
        ) : (
          perms.canExport && (
            <Button size="sm" color="secondary" onClick={exportDeliveries}>
              <Icon name="download/download-down" />
              <HideText notAbove="lg">Export</HideText>
            </Button>
          )
        )}
      </Header.Major>
      <Page.Content>
        <VendorDeliveriesDataGrid
          rows={deliveries}
          rowCount={count}
          targetUserId={targetUserId}
          currentUser={currentUser}
          loading={loading}
          disableColumnReorder
          pageSizeOptions={[15]}
          paginationModel={{
            pageSize: 15,
            page: params?.page ? params.page - 1 : 0,
          }}
          onPaginationModelChange={(model) => {
            updateUrl('page', model.page + 1);
          }}
          sortModel={
            params?.sortBy
              ? [
                  {
                    field: params.sortBy,
                    sort: params.sortOrder === 'ASC' ? 'asc' : 'desc',
                  },
                ]
              : []
          }
          onSortModelChange={(model) => {
            if (model.length > 0) {
              updateUrlHeader(undefined, model[0].field);
            } else {
              removeSortParams();
            }
          }}
        />
      </Page.Content>

      <RemoveBookingModal
        booking={selectedDelivery || {}}
        open={openRemoveModal}
        locations={locations}
        onClose={() => toggleRemoveModal(!openRemoveModal)}
        onRemove={(delivery, reason) => {
          removeDelivery(targetUserId, delivery.id, reason);
          toggleRemoveModal(!openRemoveModal);
          setSelectedDelivery(null);
        }}
      />

      {!isEmpty(locations) && !isEmpty(vendor) && (
        <DeliveryModal
          superuser={includes(
            [UserRoles.SCHEDULER, UserRoles.ADMIN],
            currentUser.role,
          )}
          availabilities={availabilities}
          loading={loading}
          currentUserId={targetUserId}
          delivery={selectedDelivery || {}}
          deliverablePurchaseOrders={deliverablePurchaseOrders}
          getAvailabilities={getAvailabilities}
          getDeliverablePurchaseOrders={getDeliverablePurchaseOrders}
          locations={locations}
          defaultLocation={get(
            locations,
            `${vendor.default_delivery_location}`,
          )}
          onClose={() => {
            toggleDeliveryModal(!openDeliveryModal);
            setSelectedDelivery(null);
          }}
          open={openDeliveryModal}
          saveDelivery={saveDeliveryV2}
          vendor={vendor}
          vendorView
        />
      )}
    </Page.Container>
  );
}

VendorDeliveries.propTypes = {
  availabilities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  createDelivery: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired,
  currentUser: PropTypes.shape({}).isRequired,
  deliverablePurchaseOrders: PropTypes.shape({}).isRequired,
  deliveries: PropTypes.shape({}).isRequired,
  getDeliveries: PropTypes.func.isRequired,
  getLocations: PropTypes.func.isRequired,
  getAvailabilities: PropTypes.func.isRequired,
  getDeliverablePurchaseOrders: PropTypes.func.isRequired,
  history: historyProps.isRequired,
  loading: PropTypes.bool.isRequired,
  location: PropTypes.shape({}).isRequired,
  locations: PropTypes.shape({}).isRequired,
  removeDelivery: PropTypes.func.isRequired,
  updateDelivery: PropTypes.func.isRequired,
  vendor: vendorProps.isRequired,
};
